import en from './pageMeta/en.json'
import it from './pageMeta/it.json'

/**
 * Address and texts of the pages, one file per language.
 *
 * A page is named as its route (see router/index.js) and its entry holds the address it has in that
 * language: the same page is /events in English and /eventi in Italian, so the address of the page
 * follows the language and is written here, once, for the client and the server both. The files are
 * copied next to the site by the build (see vite.config.js) so that meta.php, which cannot read the
 * bundles, answers the crawlers with the very same addresses and texts.
 *
 * A page with no title is a page nobody shares (the ones only a logged-in user sees): it has an
 * address all the same, and both readers leave its metadata to `default`.
 */
const languages = { en, it }

/**
 * Address of every page in a language, in the syntax of the router (`/players/{username}` becomes
 * `/players/:username`).
 */
export function pagePaths(language)
{
  const resource = languages[language] ?? languages.en

  return Object.fromEntries(
    Object.entries(resource.pages).map(([name, page]) => [name, routerPath(page.path)]),
  )
}

/**
 * Address of a page in a language, with the values in place (/players/{username} -> /players/Azzibbo),
 * or null when that language does not have the page. The language is not in it: the router and meta.php
 * write that in front (see withLanguage in router/index.js).
 */
export function pageAddress(language, name, params = {})
{
  const path = (languages[language] ?? languages.en).pages[name]?.path

  return path === undefined ? null : (fill(path, params) ?? path)
}

/**
 * Name and values of the page an address answers to, or null when no page has that address. It is the
 * reverse of `pagePaths`, and it is how an address written in one language is read as the same page in
 * another one (see settleAddress in router/index.js).
 */
export function pageOfPath(language, path)
{
  const resource = languages[language] ?? languages.en

  for (const [name, page] of Object.entries(resource.pages)) {
    const params = matchPath(page.path, path)

    if (params !== null) {
      return { name, params }
    }
  }

  return null
}

/**
 * Title and description of a page, or the generic ones when the page has none of its own or one of
 * its values is missing (a track id that does not exist).
 */
export function pageMeta(language, name, params = {})
{
  const resource = languages[language] ?? languages.en
  const entry = resource.pages[name]
  const track = resource.tracks[params.trackId ?? params.trackKey]
  const values = track ? { ...params, track: track.name, country: track.country } : params

  if (!entry?.title) {
    return { ...resource.default }
  }

  return {
    title: fill(entry.title, values) ?? resource.default.title,
    description: fill(entry.description, values) ?? resource.default.description,
  }
}

/** Address as the router writes it: /players/{username} -> /players/:username. */
function routerPath(path)
{
  return path.replace(/\{(\w+)\}/g, ':$1')
}

/** Text with the `{name}` replaced by their value, or null when one of them has no value. */
function fill(text, values)
{
  let missing = false

  const filled = text.replace(/\{(\w+)\}/g, (whole, name) => {
    if (!(name in values)) {
      missing = true

      return ''
    }

    return values[name]
  })

  return missing ? null : filled
}

/**
 * Values an address in the `{name}` form takes from a path ({} when it has none), or null when the path
 * is a different address.
 */
function matchPath(address, path)
{
  const pattern = address.split('/')
  const wanted = path.split('/')

  if (pattern.length !== wanted.length) {
    return null
  }

  const params = {}

  for (const [index, segment] of pattern.entries()) {
    const name = /^\{(\w+)\}$/.exec(segment)

    if (name !== null) {
      params[name[1]] = decodeURIComponent(wanted[index])
      continue
    }

    if (segment !== wanted[index]) {
      return null
    }
  }

  return params
}
